import { FormGroup, FormArray, Validators } from '@angular/forms';

/**
 * Utility functions for form handling
 * 
 * This utility provides methods to manage form error states, validation, and user experience.
 * 
 * @example
 * ```typescript
 * import { FormUtils } from 'src/app/core/utils/form.utils';
 * 
 * // Show error states for required fields only
 * FormUtils.showFormErrorStates(this.form);
 * 
 * // Show error states for all fields including FormArrays
 * FormUtils.showAllFormErrorStates(this.form);
 * 
 * // Show error states with a delay (useful for async form initialization)
 * FormUtils.showFormErrorStatesWithDelay(this.form, 200);
 * 
 * // Reset error states
 * FormUtils.resetFormErrorStates(this.form);
 * ```
 */
export class FormUtils {

  /**
   * Shows error states for all required fields in a form by marking them as touched and dirty
   * This is useful for showing validation errors immediately when a form loads
   * 
   * @param form - The FormGroup to show error states for
   * 
   * @example
   * ```typescript
   * ngOnInit() {
   *   this.initForm();
   *   FormUtils.showFormErrorStates(this.form);
   * }
   * ```
   */
  static showFormErrorStates(form: FormGroup): void {
    // Mark form as touched and dirty to show error states by default
    form.markAsTouched();
    form.markAsDirty();

    // Mark all required fields as touched to show their error states
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control && control.hasValidator(Validators.required)) {
        control.markAsTouched();
      }
    });

    form.updateValueAndValidity();
  }

  /**
   * Shows error states for all form controls (including FormArrays) by marking them as touched and dirty
   * This is more aggressive than showFormErrorStates and will show errors for all fields, not just required ones
   * 
   * @param form - The FormGroup to show error states for
   * 
   * @example
   * ```typescript
   * // Show errors for all fields including nested FormArrays
   * FormUtils.showAllFormErrorStates(this.form);
   * ```
   */
  static showAllFormErrorStates(form: FormGroup): void {
    // Mark the entire form as touched and dirty
    form.markAsTouched();
    form.markAsDirty();

    // Mark all form controls as touched to trigger error state display
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();

        // If it's a FormArray, mark each control as touched
        if (control instanceof FormArray) {
          control.controls.forEach(arrayControl => {
            arrayControl.markAsTouched();
            arrayControl.markAsDirty();
          });
        }
      }
    });

    // Trigger validation to show error states
    form.updateValueAndValidity();
  }

  /**
   * Resets form error states by marking all controls as untouched and pristine
   * This hides all validation errors and resets the form's visual state
   * 
   * @param form - The FormGroup to reset error states for
   * 
   * @example
   * ```typescript
   * // Hide all error states
   * FormUtils.resetFormErrorStates(this.form);
   * ```
   */
  static resetFormErrorStates(form: FormGroup): void {
    // Reset form state to not show errors
    form.markAsUntouched();
    form.markAsPristine();

    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();

        if (control instanceof FormArray) {
          control.controls.forEach(arrayControl => {
            arrayControl.markAsUntouched();
            arrayControl.markAsPristine();
          });
        }
      }
    });
  }

  /**
   * Shows error states for a form after a specified delay
   * Useful when form initialization is asynchronous or when you want to delay error display
   * 
   * @param form - The FormGroup to show error states for
   * @param delay - Delay in milliseconds (default: 100ms)
   * 
   * @example
   * ```typescript
   * // Show errors after 200ms delay
   * FormUtils.showFormErrorStatesWithDelay(this.form, 200);
   * ```
   */
  static showFormErrorStatesWithDelay(form: FormGroup, delay: number = 100): void {
    setTimeout(() => {
      this.showFormErrorStates(form);
    }, delay);
  }

  /**
   * Shows all error states for a form after a specified delay
   * Useful when form initialization is asynchronous and you want to show all field errors
   * 
   * @param form - The FormGroup to show error states for
   * @param delay - Delay in milliseconds (default: 100ms)
   * 
   * @example
   * ```typescript
   * // Show all errors after 200ms delay
   * FormUtils.showAllFormErrorStatesWithDelay(this.form, 200);
   * ```
   */
  static showAllFormErrorStatesWithDelay(form: FormGroup, delay: number = 100): void {
    setTimeout(() => {
      this.showAllFormErrorStates(form);
    }, delay);
  }
} 